import api from './api';

/**
 * Service for service-related API calls
 * All methods return { success: true, data } or { success: false, error }
 */

const serviceService = {
  /**
   * Fetch all service categories
   */
  async fetchServiceCategories() {
    try {
      const response = await api.get('/partners/service-categories/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching service categories:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch service categories'
      };
    }
  },

  /**
   * Fetch cities where services are available
   */
  async fetchCities() {
    try {
      const response = await api.get('/services/cities/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching service cities:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch cities'
      };
    }
  },

  /**
   * Fetch services with optional filters
   * @param {Object} params - Query parameters (service_category, partner__city, search, ordering)
   */
  async fetchServices(params = {}) {
    try {
      const response = await api.get('/services/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching services:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch services'
      };
    }
  },

  /**
   * Fetch service detail by ID
   * @param {number} serviceId
   */
  async fetchServiceDetail(serviceId) {
    try {
      const response = await api.get(`/services/${serviceId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching service detail:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch service details'
      };
    }
  },

  /**
   * Fetch available time slots for a service on a specific date
   * @param {number} serviceId
   * @param {string} date - Date in YYYY-MM-DD format
   */
  async fetchAvailableSlots(serviceId, date) {
    try {
      const response = await api.get(`/services/${serviceId}/available-slots/`, {
        params: { date }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching available slots:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || 'Failed to fetch available slots'
      };
    }
  },

  // Partner endpoints

  /**
   * Fetch partner's own services
   */
  async fetchPartnerServices() {
    try {
      const response = await api.get('/services/partner/services/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner services:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch partner services'
      };
    }
  },

  /**
   * Create a new service
   * @param {Object} serviceData
   */
  async createService(serviceData) {
    try {
      const response = await api.post('/services/partner/services/', serviceData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating service:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to create service'
      };
    }
  },

  /**
   * Update a service
   * @param {number} serviceId
   * @param {Object} serviceData
   */
  async updateService(serviceId, serviceData) {
    try {
      const response = await api.patch(`/services/partner/services/${serviceId}/`, serviceData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating service:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to update service'
      };
    }
  },

  /**
   * Delete a service
   * @param {number} serviceId
   */
  async deleteService(serviceId) {
    try {
      await api.delete(`/services/partner/services/${serviceId}/`);
      return { success: true, data: null };
    } catch (error) {
      console.error('Error deleting service:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete service'
      };
    }
  },

  /**
   * Fetch service availability
   * @param {number} serviceId
   */
  async fetchServiceAvailability(serviceId) {
    try {
      const response = await api.get(`/services/partner/services/${serviceId}/availability/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching service availability:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch service availability'
      };
    }
  },

  /**
   * Add availability to a service
   * @param {number} serviceId
   * @param {Object} availabilityData - { day_of_week, start_time, end_time }
   */
  async addServiceAvailability(serviceId, availabilityData) {
    try {
      const response = await api.post(`/services/partner/services/${serviceId}/availability/`, availabilityData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error adding service availability:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to add service availability'
      };
    }
  },

  /**
   * Delete service availability
   * @param {number} serviceId
   * @param {number} availabilityId
   */
  async deleteServiceAvailability(serviceId, availabilityId) {
    try {
      await api.delete(`/services/partner/services/${serviceId}/availability/${availabilityId}/`);
      return { success: true, data: null };
    } catch (error) {
      console.error('Error deleting service availability:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete service availability'
      };
    }
  },

  /**
   * Fetch service holidays
   * @param {number} serviceId
   */
  async fetchServiceHolidays(serviceId) {
    try {
      const response = await api.get(`/services/partner/services/${serviceId}/holidays/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching service holidays:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch service holidays'
      };
    }
  },

  /**
   * Add holiday to a service
   * @param {number} serviceId
   * @param {Object} holidayData - { date, reason }
   */
  async addServiceHoliday(serviceId, holidayData) {
    try {
      const response = await api.post(`/services/partner/services/${serviceId}/holidays/`, holidayData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error adding service holiday:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to add service holiday'
      };
    }
  },

  /**
   * Delete service holiday
   * @param {number} serviceId
   * @param {number} holidayId
   */
  async deleteServiceHoliday(serviceId, holidayId) {
    try {
      await api.delete(`/services/partner/services/${serviceId}/holidays/${holidayId}/`);
      return { success: true, data: null };
    } catch (error) {
      console.error('Error deleting service holiday:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete service holiday'
      };
    }
  }
};

export default serviceService;
